import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { cn } from "@/core/helpers/cn";
import useClickAway from "@/core/helpers/useClickAway";
import { ArrowDownIcon } from "@/shared/icons";
import { Button, Icon, Select } from "@/shared/ui";
import { FactionEnum } from "@/domains/faction/constants/factionEnum";
import { SelectItem } from "@/domains/faction/constants/selectItem";
import FactionOptionModal from "@/domains/faction/components/FactionOptionModal";

type Props = {
  title: string;
  selectsList: SelectItem[];
  currentId: number;
  onClose: () => void;
  onConfirm: (id: number) => void;
  onChangeSelect: (id: number) => void;
  isLoading?: boolean;
  isConfirming: boolean;
  faction: FactionEnum;
};

export default function FactionSelectModal({
  title,
  selectsList,
  currentId,
  onClose,
  onConfirm,
  onChangeSelect,
  isLoading,
  isConfirming,
  faction,
}: Props) {
  const { t } = useTranslation("faction");

  const ref = useClickAway(onClose);

  const selectedItem = selectsList?.find((item) => item.isActive);

  let gradient = "oliveShadow";
  if (faction === FactionEnum.HOSPITAL) {
    gradient = "redShadow";
  }
  if (faction === FactionEnum.MEDSI || faction === FactionEnum.REGION24) {
    gradient = "lightBlueShadow";
  }

  return (
    <div
      className={clsx(
        "absolute z-10 top-0 left-0 right-0 bottom-0 bg-[#0F0F0F]/95",
        "flex items-center justify-center",
        "animate-[--animation-fade-in]",
      )}
    >
      {isLoading && (
        <div className="flex items-center justify-center p-4">
          <div className="mz-loader" />
        </div>
      )}

      {!isLoading && (
        <div
          ref={ref}
          className={cn(
            "w-[25.125rem] p-5 bg-gradient-to-b from-[#0F0F0F] to-[#0F0F0F]/90 rounded-[0.875rem]",
            "relative p-7.5 flex flex-col items-center",
            gradient,
          )}
        >
          <Button
            variant="surface"
            color="gray"
            className="absolute top-2.5 right-2.5 size-7.5 p-0"
            onClick={onClose}
          >
            <Icon name="close" className="w-3" />
          </Button>
          <div className="text-18 font-bold text-white">{title}</div>
          <div className="h-px w-[20rem] bg-[#1F1F1F]/80 flex justify-center my-5">
            <div className="h-full w-[5rem] bg-accent-solid" />
          </div>
          <Select
            className="w-full h-14.5 mb-5 rounded-xl"
            classNameSelect={cn("p-3.5 !rounded-xl", "#405C4A")}
            classNamePlaceholder="text-[0.75rem] font-bold text-white uppercase "
            placeholder={selectedItem?.name}
            Icon={ArrowDownIcon}
            classNameIcon="right-3.5 size-3.5"
            optionsStyle={{ boxShadow: "none" }}
            classNameOptopns="z-10 top-13 pt-1 rounded-b-2xl"
          >
            <div className="flex flex-col gap-[0.8125rem] border-t border-t-white/8">
              <div className="relative flex flex-col gap-[0.4375rem] max-h-[17.6875rem] pr-1.5 my-3.5 ml-3.5 mr-1.5 overflow-y-auto overflow-x-clip [--scrollbar-track-bg:#181818] [--scrollbar-thumb-bg:#434242] [scrollbar-gutter:stable] [&::-webkit-scrollbar]:w-0.5">
                {selectsList?.length > 0 &&
                  selectsList.map((item) => (
                    <FactionOptionModal
                      key={item.id}
                      id={item.id}
                      text={item.name}
                      isActive={item.id === currentId}
                      onClick={onChangeSelect}
                    />
                  ))}
              </div>
            </div>
          </Select>
          <div className="grid grid-cols-2 gap-2 w-full">
            <Button
              className="gap-2.5 h-11"
              onClick={() => onConfirm(selectedItem?.id)}
              loading={isConfirming}
              disabled={isConfirming}
            >
              {t("faction.change")}
            </Button>
            <Button
              variant="solid-surface"
              className="h-11"
              onClick={onClose}
              loading={isConfirming}
              disabled={isConfirming}
            >
              {t("faction.cancel")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
